import axiosInstance from "./axiosConfig";
import type { WorkflowType } from "../types/workflow.type";

export type IndividualApiResponse = Record<string, unknown>;

export type IndividualUpdateRequest = Record<string, unknown>;

const BASE_URL = "/individuals";

export type IndividualCreateRequest = {
  firstName: string;
  lastName: string;
  email: string;
  visa?: string;
  title?: string;
  pronouns?: string;
  is_rfe?: boolean;
  workflow_type?: WorkflowType;
};

const firstDefined = <T>(
  obj: Record<string, unknown>,
  keys: string[]
): T | undefined => {
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null) return value as T;
  }
  return undefined;
};

const monthAbbrevToNumber: Record<string, string> = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12"
};

const parseDateToYmd = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";

  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  // ISO datetime -> YYYY-MM-DD
  if (trimmed.includes("T") && /^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    return trimmed.split("T")[0];
  }

  // Flask often returns RFC1123 like: "Sat, 06 Jul 1907 00:00:00 GMT"
  const rfc1123Match = trimmed.match(/,\s*(\d{2})\s+([A-Za-z]{3})\s+(\d{4})\s/);
  if (rfc1123Match) {
    const day = rfc1123Match[1];
    const mon = monthAbbrevToNumber[rfc1123Match[2]] ?? "01";
    const year = rfc1123Match[3];
    return `${year}-${mon}-${day}`;
  }

  // Last resort: try Date.parse
  const parsedMs = Date.parse(trimmed);
  if (Number.isFinite(parsedMs)) {
    const d = new Date(parsedMs);
    if (!Number.isNaN(d.getTime())) return d.toISOString().split("T")[0];
  }

  return "";
};

const ymdToIsoDatetime = (ymd: string): string | undefined => {
  if (!ymd) return undefined;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return undefined;
  // Avoid timezone shifting by pinning to noon UTC.
  const d = new Date(`${ymd}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

export type AddressEditValues = {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
};

const normalizeAddress = (raw: unknown): AddressEditValues => {
  const obj = (raw ?? {}) as Record<string, unknown>;
  return {
    street: (firstDefined<string>(obj, ["street"]) ?? "") || "",
    city: (firstDefined<string>(obj, ["city"]) ?? "") || "",
    state: (firstDefined<string>(obj, ["state"]) ?? "") || "",
    zipCode:
      (firstDefined<string>(obj, ["zipCode", "zip_code"]) ?? "")?.toString() ||
      "",
    country: (firstDefined<string>(obj, ["country"]) ?? "") || ""
  };
};

export type EmployerInfoEditValues = {
  legalBusinessName: string;
  dba: string;
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  businessAddress: AddressEditValues;
};

export type IndividualEditValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  pronouns: string;
  dob: string; // stored as YYYY-MM-DD for input[type=date]
  countryOfBirth: string;
  countryOfCitizenship: string;
  email: string;
  phone: string;
  visaType: string;
  isRfe: boolean;
  physicalAddress: AddressEditValues;
  mailingAddress: AddressEditValues;
  similarAddress: boolean;
  employerInfo: EmployerInfoEditValues;
  employerPetitioner: boolean;
};

export const IndividualsAPI = {
  async createIndividual(
    data: IndividualCreateRequest
  ): Promise<IndividualApiResponse> {
    // Backend: POST /api/individuals (baseURL typically already includes /api).
    // Prefer snake_case to match backend aliases.
    const payload: Record<string, unknown> = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email
    };

    if (data.visa) payload.visa = data.visa;
    if (data.title) payload.title = data.title;
    if (data.pronouns) payload.pronouns = data.pronouns;

    const response = await axiosInstance.post(`${BASE_URL}`, payload);
    return response.data as IndividualApiResponse;
  },

  async getIndividual(individualId: string): Promise<IndividualApiResponse> {
    const response = await axiosInstance.get(`${BASE_URL}/${individualId}`);
    return response.data as IndividualApiResponse;
  },

  // NOTE: Backend update endpoint is not provided here; this follows the common
  // pattern used across the app. If your backend uses PATCH or a different route,
  // adjust accordingly.
  async updateIndividual(
    individualId: string,
    data: IndividualUpdateRequest
  ): Promise<IndividualApiResponse> {
    const response = await axiosInstance.put(
      `${BASE_URL}/${individualId}`,
      data
    );
    return response.data as IndividualApiResponse;
  },

  normalizeForEditForm(raw: IndividualApiResponse): IndividualEditValues {
    const firstName =
      (firstDefined<string>(raw, ["firstName", "first_name"]) ?? "") || "";
    const middleName =
      (firstDefined<string>(raw, ["middleName", "middle_name"]) ?? "") || "";
    const lastName =
      (firstDefined<string>(raw, ["lastName", "last_name"]) ?? "") || "";
    const title = (firstDefined<string>(raw, ["title"]) ?? "") || "";
    const pronouns = (firstDefined<string>(raw, ["pronouns"]) ?? "") || "";
    const dob = parseDateToYmd(
      firstDefined<string>(raw, ["birthday", "dob"]) ?? ""
    );
    const countryOfBirth =
      (firstDefined<string>(raw, ["countryob", "country_of_birth"]) ?? "") ||
      "";
    const countryOfCitizenship =
      (firstDefined<string>(raw, ["citizenship", "country_of_citizenship"]) ??
        "") ||
      "";
    const email = (firstDefined<string>(raw, ["email"]) ?? "") || "";
    const phone = (firstDefined<string>(raw, ["phone"]) ?? "") || "";
    const visaType =
      (firstDefined<string>(raw, ["visaType", "visa_type", "visa"]) ?? "") ||
      "";
    const isRfe = Boolean(firstDefined<boolean>(raw, ["is_rfe", "isRfe"]));
    const physicalAddress = normalizeAddress(
      firstDefined<Record<string, unknown>>(raw, [
        "physicalAddress",
        "physical_address"
      ]) ?? {}
    );
    const mailingAddress = normalizeAddress(
      firstDefined<Record<string, unknown>>(raw, [
        "mailingAddress",
        "mailing_address"
      ]) ?? {}
    );
    const similarAddress = Boolean(
      firstDefined<boolean>(raw, ["similarAddress", "similar_address"])
    );

    const employerInfo = (firstDefined<Record<string, unknown>>(raw, [
      "employerInfo",
      "employer_info"
    ]) ?? {}) as Record<string, unknown>;

    const employerInfoValues: EmployerInfoEditValues = {
      legalBusinessName:
        (firstDefined<string>(employerInfo, [
          "legalBusinessName",
          "legal_business_name"
        ]) ??
          "") ||
        "",
      dba: (firstDefined<string>(employerInfo, ["dba"]) ?? "") || "",
      contactName:
        (firstDefined<string>(employerInfo, ["contactName", "contact_name"]) ??
          "") ||
        "",
      contactTitle:
        (firstDefined<string>(employerInfo, [
          "contactTitle",
          "contact_title"
        ]) ??
          "") ||
        "",
      contactEmail:
        (firstDefined<string>(employerInfo, [
          "contactEmail",
          "contact_email"
        ]) ??
          "") ||
        "",
      contactPhone:
        (firstDefined<string>(employerInfo, [
          "contactPhone",
          "contact_phone"
        ]) ??
          "") ||
        "",
      businessAddress: normalizeAddress(
        firstDefined<Record<string, unknown>>(employerInfo, [
          "businessAddress",
          "business_address"
        ]) ?? {}
      )
    };

    const employerPetitioner = Boolean(
      firstDefined<boolean>(raw, ["employerPetitioner", "employer_petitioner"])
    );

    return {
      firstName,
      middleName,
      lastName,
      title,
      pronouns,
      dob,
      countryOfBirth,
      countryOfCitizenship,
      email,
      phone,
      visaType,
      isRfe,
      physicalAddress,
      mailingAddress,
      similarAddress,
      employerInfo: employerInfoValues,
      employerPetitioner
    };
  },

  toUpdatePayload(values: IndividualEditValues): IndividualUpdateRequest {
    // Prefer camelCase keys (matches most of the app). If the backend expects
    // snake_case, swap these keys accordingly.
    return {
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      title: values.title,
      pronouns: values.pronouns,
      // Flask expects `birthday` (datetime).
      birthday: ymdToIsoDatetime(values.dob),
      countryob: values.countryOfBirth,
      citizenship: values.countryOfCitizenship,
      email: values.email,
      phone: values.phone,
      visaType: values.visaType,
      is_rfe: values.isRfe,
      physicalAddress: {
        street: values.physicalAddress.street,
        city: values.physicalAddress.city,
        state: values.physicalAddress.state,
        country: values.physicalAddress.country,
        zip_code: values.physicalAddress.zipCode
      },
      mailingAddress: {
        street: values.mailingAddress.street,
        city: values.mailingAddress.city,
        state: values.mailingAddress.state,
        country: values.mailingAddress.country,
        zip_code: values.mailingAddress.zipCode
      },
      similarAddress: values.similarAddress,
      employerInfo: {
        legalBusinessName: values.employerInfo.legalBusinessName,
        dba: values.employerInfo.dba,
        contactName: values.employerInfo.contactName,
        contactTitle: values.employerInfo.contactTitle,
        contactEmail: values.employerInfo.contactEmail,
        contactPhone: values.employerInfo.contactPhone,
        businessAddress: {
          street: values.employerInfo.businessAddress.street,
          city: values.employerInfo.businessAddress.city,
          state: values.employerInfo.businessAddress.state,
          country: values.employerInfo.businessAddress.country,
          zip_code: values.employerInfo.businessAddress.zipCode
        }
      },
      employerPetitioner: values.employerPetitioner
    };
  }
};
